import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, Link, useRouteMatch } from "react-router-dom";
import { selectMyUser } from "../../models/users/myUserSlice";
import { Role } from "../../models/users/userInterfaces";
import { canAccess } from "../../utils/user";
import { selectOrg } from "../../models/organization/organizationSlice";
import { mixpanel } from "../../utils/mixpanel";
import {
  GraphUp,
  InboxFill,
  CaretRightFill,
  Gear,
  PersonWorkspace,
  GridFill,
} from "react-bootstrap-icons";

import "./navigation.scss";

interface NavItem {
  id: string;
  title?: string;
  path?: string;
  roles: Role[];
  externalUrl?: string;
}

const Navigation: FC = () => {
  const { pathname } = useLocation();
  const myUser = useSelector(selectMyUser);
  const organization = useSelector(selectOrg);
  let { url } = useRouteMatch();

  const renderIcon = (path: string, className?: string) => {
    switch (path) {
      case "agenticAI":
        return <GridFill className={className} />;
      case "settings":
        return <Gear className={className} />;
      case "analytics":
        return <GraphUp className={className} />;
      case "links":
        return <PersonWorkspace className={className} />;
      case "applications":
        return <InboxFill className={className} />;
      case "audienceBuilder":
        return <PersonWorkspace className={className} />;
      default:
        return <CaretRightFill />;
    }
  };

  const settingsBaseUrl =
    organization.meta?.settingsBaseUrl ||
    process.env.REACT_APP_SETTINGS_BASE_URL;

  const agenticAIBaseUrl =
    organization.meta?.agenticAIBaseUrl ||
    process.env.REACT_APP_AGENTIC_AI_BASE_URL;
  const customMenu = organization?.meta?.customMenu;

  // Normally, button will display only if a promo URL and label is provided. For scenarios where these are defaulted by the env, "disabled" can be used to hide the button.
  const promoUrl =
    organization.meta?.promoUrl !== "disabled"
      ? organization.meta?.promoUrl || process.env.REACT_APP_PROMO_URL
      : undefined;
  const promoUrlWithTracking = `${promoUrl}?utm_source=${myUser?.userId}`;
  const promoLabel =
    organization.meta?.promoLabel || process.env.REACT_APP_PROMO_LABEL;

  // * If the user has an email, make it available for external links as a query param
  //   Encode it so that characters like "+" become "%2B"
  const emailQueryString = myUser?.email
    ? `?login_id=${encodeURIComponent(myUser.email)}`
    : "";

  // Store default navItems
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];
    if (settingsBaseUrl) {
      items.push({
        id: "settings",
        title: "Settings",
        externalUrl: `${settingsBaseUrl}/organization/${organization.slug}/login${emailQueryString}`,
        roles: ["portalAdmin"],
      });
    }

    items.push({
      id: "applications",
      title: "Applications",
      path: "applications",
      roles: ["portalAdmin", "analyst", "portalUser"],
    });
    if (organization?.meta?.analyticsEnabled) {
      items.push({
        id: "analytics",
        title: "Analytics",
        path: "analytics",
        roles: ["portalAdmin", "analyst"],
      });
    }
    // Agentic AI is rendered as a submenu in the header below (AI Growth Agents
    // + Audience Builder), not a flat nav item — so it is not pushed here.
    return items;
  }, [organization, emailQueryString, settingsBaseUrl, agenticAIBaseUrl]);

  const filteredNavItems = navItems
    .filter((item) => {
      if (!myUser?.roles) return false;
      return canAccess(myUser?.roles, item.roles);
    })
    .reverse();

  const trackClick = (nav: NavItem) => {
    mixpanel.track("Button Click", {
      "Button Title": nav.title,
      "Button Path": nav.path,
      "User Id": myUser?.userId,
      "Organization ID": organization.id,
      "Organization Name": organization.name,
      "Host Name": window.location.hostname,
      "Page URL": window.location.href,
    });
  };

  return (
    <div className="nav-header">
      {/* PROMO BUTTON */}
      {promoUrl && promoLabel && myUser?.isAdminPortalUser && (
        <a
          key={"promotion"}
          href={promoUrlWithTracking}
          target="blank"
          className="nav-header-promo"
        >
          <span
            className="mr-2"
            style={{ color: "white", fontWeight: "bolder", fontSize: "12px" }}
          >
            NEW
          </span>
          <span style={{ color: "black" }}>{promoLabel}</span>
        </a>
      )}

      {/* TODO: If this needs to be available for non-admin users, we need to make sure it is also available on mobile. */}
      {/* CUSTOM MENU */}
      {customMenu && myUser?.isAdminPortalUser && (
        <div className="dropdown px-2">
          <div
            className="dropdown-toggle nav-header-tab"
            id="link-toggle"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            style={{ cursor: "pointer" }}
          >
            <div className="mr-2 d-inline nav-icon">
              {renderIcon(customMenu?.icon || "links")}
            </div>
            <span style={{ color: "#010101" }}>{customMenu.title}</span>
          </div>
          <div
            className="border-highlight dropdown-menu dropdown-menu-right "
            aria-labelledby="dropdownMenuButton"
          >
            {customMenu.links?.map((link) => (
              <a
                className="dropdown-item"
                href={link.action}
                target={link?.target || "_blank"}
              >
                {renderIcon(link.icon || "default", "mr-2 d-inline nav-icon")}
                {link.title}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* AGENTIC AI (submenu: AI Growth Agents + Audience Builder) */}
      {agenticAIBaseUrl &&
        myUser?.roles &&
        canAccess(myUser.roles, ["portalAdmin"]) && (
          <div className="dropdown px-2">
            <div
              className="dropdown-toggle nav-header-tab"
              id="agentic-ai-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              style={{ cursor: "pointer" }}
            >
              <div className="mr-2 d-inline nav-icon">
                {renderIcon("agenticAI")}
              </div>
              <span style={{ color: "#010101" }}>Agentic AI</span>
            </div>
            <div
              className="border-highlight dropdown-menu dropdown-menu-right "
              aria-labelledby="agentic-ai-toggle"
            >
              <a
                className="dropdown-item"
                href={`${agenticAIBaseUrl}/?org=${organization.slug}`}
                rel="noopener noreferrer"
              >
                {renderIcon("agenticAI", "mr-2 d-inline nav-icon")}
                AI Growth Agents
              </a>
              <a
                className="dropdown-item"
                href={`/audience-builder/organization/${organization.slug}/audiences`}
              >
                {renderIcon("audienceBuilder", "mr-2 d-inline nav-icon")}
                Audience Builder
              </a>
            </div>
          </div>
        )}

      {/* STANDARD MENU - MAPPED NAV ITEMS */}
      {filteredNavItems.map((nav) => {
        const linkContent = (
          <div
            className={`nav-header-tab ${
              pathname.includes(nav.id) ? "active" : ""
            }`}
          >
            {renderIcon(nav.id, "mr-2 d-inline nav-icon")}
            <span style={{ color: "#010101" }}>{nav.title}</span>
          </div>
        );

        // STANDARD BUTTONS
        return nav.externalUrl ? (
          <a key={nav.id} href={nav.externalUrl} rel="noopener noreferrer">
            {linkContent}
          </a>
        ) : (
          <Link
            key={nav.id}
            to={`${url}/${nav.path}`}
            onClick={() => trackClick(nav)}
          >
            {linkContent}
          </Link>
        );
      })}
    </div>
  );
};

export default Navigation;
